/**
 * Minimal Tableau calc-formula classifier.
 *
 * PR7 table-calc lowering slice 1. This module owns the narrow grammar
 * needed to recognize calc fields we can lower into `TypedWindowMeasure`
 * entries. It intentionally does NOT parse the full Tableau calc
 * sublanguage — anything outside the recognized shapes returns a typed
 * rejection reason so the typed-spec gate stays conservative and
 * unrecognized shapes hard fail under production strictness.
 *
 * Supported shapes (after whitespace normalization):
 *
 *   1. `INDEX()`
 *        → { kind: 'index' }
 *
 *   2. `LOOKUP(<agg>, <offset>)`
 *        where `<agg>` is an aggregation over a single physical field,
 *        optionally wrapped in `ZN(...)` (zero-if-null). The aggregation
 *        may be explicit (`SUM([field])`, `AVG([field])`, `MIN([field])`,
 *        `MAX([field])`, `COUNT([field])`, `COUNTD([field])`) or an
 *        "aggregation-omitted" bare reference (`([field])`), which the
 *        caller must resolve against the authored-shelf aggregation for
 *        that field.
 *        and `<offset>` is:
 *          - an integer literal (positive or negative) → Lag(N)/Lead(-N)
 *          - `FIRST()` → First
 *          - `LAST()`  → Last
 *        → { kind: 'lookup', base, offset }
 *
 *   3. `WINDOW_<agg>(<agg>)` / `-WINDOW_<agg>(<agg>)`
 *        where `<agg>` is the same single-field aggregate or bare
 *        reference grammar used by LOOKUP.
 *        → { kind: 'window_agg', windowKind, base, negate }
 *
 * The classifier is deterministic, allocation-light, and depends on
 * nothing outside this module. It is the ONE place we grow Tableau calc
 * formula support; upstream callers (see `buildTypedSpecFromAuthoring.ts`)
 * MUST prefer adding cases here over matching formulas ad-hoc.
 *
 * Mirror in Rust: formulas that classify to a supported kind lower into
 * `TypedWindowKind::{Index, Lag, Lead, First, Last, Window*}` via
 * `query_builder/src/worksheet_compiler/typed_spec.rs`. The Rust
 * compiler already emits SQL for every kind this module recognizes;
 * widening the grammar here is an authoring-layer concern only.
 */

export type ClassifiedCalcAggregation =
  | { kind: 'explicit'; agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'; field: string }
  // Bare reference like `([productnet])` inside a LOOKUP. Tableau infers
  // the aggregation from the authored shelf default; we surface this to
  // the caller so it can resolve via the worksheet's measure pills.
  | { kind: 'bare'; field: string }

export type ClassifiedLookupOffset =
  | { kind: 'int'; value: number }
  | { kind: 'first' }
  | { kind: 'last' }

export type ClassifiedCalcKind =
  | { kind: 'index' }
  | {
      kind: 'lookup'
      base: ClassifiedCalcAggregation
      offset: ClassifiedLookupOffset
      zeroIfNull: boolean
    }
  | {
      kind: 'window_agg'
      windowKind: 'WINDOW_SUM' | 'WINDOW_AVG' | 'WINDOW_MIN' | 'WINDOW_MAX' | 'WINDOW_COUNT'
      base: ClassifiedCalcAggregation
      negate: boolean
    }

export type ClassifyCalcFormulaOutcome =
  | { ok: true; classified: ClassifiedCalcKind }
  | { ok: false; reason: string }

/**
 * Strip SQL-style `//...` and `/* ... *\/` comments, normalize internal
 * whitespace to single spaces, and trim. Tableau authoring formulas
 * routinely contain comments and line breaks; normalization here keeps
 * every downstream matcher single-line.
 */
function normalizeFormula(raw: string): string {
  let out = String(raw ?? '')
  out = out.replace(/\/\*[\s\S]*?\*\//g, ' ')
  out = out.replace(/\/\/[^\n]*/g, ' ')
  out = out.replace(/\s+/g, ' ').trim()
  return out
}

function extractBracketField(segment: string): string | null {
  const s = segment.trim()
  const m = s.match(/^\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?$/)
  if (!m) return null
  const inner = String(m[2] ?? m[1]).trim()
  if (!inner) return null
  return inner
}

/**
 * Parse `<AGG>(<fieldRef>)` or a bare `(<fieldRef>)` aggregation-omitted
 * form. `fieldRef` must be a single `[name]` token.
 */
function parseAggregation(segment: string): ClassifiedCalcAggregation | null {
  const s = segment.trim()
  const explicit = s.match(/^(SUM|AVG|MIN|MAX|COUNT|COUNTD)\s*\(\s*(.+?)\s*\)$/i)
  if (explicit) {
    const aggRaw = explicit[1].toUpperCase() as
      | 'SUM'
      | 'AVG'
      | 'MIN'
      | 'MAX'
      | 'COUNT'
      | 'COUNTD'
    const field = extractBracketField(explicit[2])
    if (!field) return null
    return { kind: 'explicit', agg: aggRaw, field }
  }
  const bare = s.match(/^\(\s*(.+?)\s*\)$/)
  if (bare) {
    const field = extractBracketField(bare[1])
    if (!field) return null
    return { kind: 'bare', field }
  }
  // Aggregation-omitted raw bracketed form `[field]` (no outer parens).
  // Tableau authors both `ZN([X])` and `ZN(([X]))` — the latter comes
  // from explicit-paren authoring, the former from the compact form.
  // Both mean "aggregation-omitted reference to X", so classify the
  // same way.
  const rawBracket = extractBracketField(s)
  if (rawBracket) {
    return { kind: 'bare', field: rawBracket }
  }
  return null
}

function parseLookupOffset(segment: string): ClassifiedLookupOffset | null {
  const s = segment.trim()
  if (/^FIRST\s*\(\s*\)$/i.test(s)) return { kind: 'first' }
  if (/^LAST\s*\(\s*\)$/i.test(s)) return { kind: 'last' }
  const int = s.match(/^(-?\d+)$/)
  if (int) {
    const n = Number(int[1])
    if (Number.isFinite(n) && Number.isInteger(n)) return { kind: 'int', value: n }
  }
  return null
}

/**
 * Split a balanced argument list like `"a, b(c, d), e"` into `["a",
 * "b(c, d)", "e"]`. Returns `null` if parentheses don't balance or the
 * string has a stray quote, so the caller can reject the formula with
 * a stable reason rather than misclassifying.
 */
function splitTopLevelArgs(inner: string): string[] | null {
  const out: string[] = []
  let depth = 0
  let buf = ''
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i]
    if (ch === '(') {
      depth++
      buf += ch
    } else if (ch === ')') {
      depth--
      if (depth < 0) return null
      buf += ch
    } else if (ch === ',' && depth === 0) {
      out.push(buf.trim())
      buf = ''
    } else {
      buf += ch
    }
  }
  if (depth !== 0) return null
  if (buf.trim().length > 0 || out.length > 0) out.push(buf.trim())
  return out
}

export function classifyCalcFormula(formula: string): ClassifyCalcFormulaOutcome {
  const f = normalizeFormula(formula)
  if (!f) return { ok: false, reason: 'calc_formula_empty' }

  // INDEX() — no arguments.
  if (/^INDEX\s*\(\s*\)$/i.test(f)) {
    return { ok: true, classified: { kind: 'index' } }
  }

  // LOOKUP(arg1, arg2) — strip an optional outer ZN(...) wrapper on
  // arg1 and classify the aggregation + offset.
  const lookup = f.match(/^LOOKUP\s*\(([\s\S]*)\)$/i)
  if (lookup) {
    const args = splitTopLevelArgs(lookup[1])
    if (!args || args.length !== 2) {
      return { ok: false, reason: 'calc_formula_lookup_arity_unexpected' }
    }
    let aggSegment = args[0].trim()
    let zeroIfNull = false
    const znMatch = aggSegment.match(/^ZN\s*\(([\s\S]*)\)$/i)
    if (znMatch) {
      zeroIfNull = true
      aggSegment = znMatch[1].trim()
    }
    const base = parseAggregation(aggSegment)
    if (!base) {
      return { ok: false, reason: 'calc_formula_lookup_base_not_aggregation' }
    }
    const offset = parseLookupOffset(args[1])
    if (!offset) {
      return { ok: false, reason: 'calc_formula_lookup_offset_unsupported' }
    }
    return { ok: true, classified: { kind: 'lookup', base, offset, zeroIfNull } }
  }

  // WINDOW_<agg>(<single-field aggregate/ref>) — Tableau's unbounded
  // table-window aggregate family. The Rust typed compiler already
  // exposes these as `TypedWindowKind::WINDOW_*`; the authoring layer's
  // job is to classify the formula and resolve the base through the
  // same calc-ref machinery as LOOKUP.
  let windowSegment = f
  let negate = false
  const negWindow = windowSegment.match(/^-\s*(WINDOW_(?:SUM|AVG|MIN|MAX|COUNT)\s*\([\s\S]*\))$/i)
  if (negWindow) {
    negate = true
    windowSegment = negWindow[1].trim()
  }
  const windowAgg = windowSegment.match(/^(WINDOW_(SUM|AVG|MIN|MAX|COUNT))\s*\(([\s\S]*)\)$/i)
  if (windowAgg) {
    const args = splitTopLevelArgs(windowAgg[3])
    if (!args || args.length !== 1) {
      return { ok: false, reason: 'calc_formula_window_agg_arity_unexpected' }
    }
    let aggSegment = args[0].trim()
    const znMatch = aggSegment.match(/^ZN\s*\(([\s\S]*)\)$/i)
    if (znMatch) aggSegment = znMatch[1].trim()
    const base = parseAggregation(aggSegment)
    if (!base) {
      return { ok: false, reason: 'calc_formula_window_agg_base_not_aggregation' }
    }
    return {
      ok: true,
      classified: {
        kind: 'window_agg',
        windowKind: windowAgg[1].toUpperCase() as
          | 'WINDOW_SUM'
          | 'WINDOW_AVG'
          | 'WINDOW_MIN'
          | 'WINDOW_MAX'
          | 'WINDOW_COUNT',
        base,
        negate,
      },
    }
  }

  return { ok: false, reason: 'calc_formula_shape_unsupported' }
}

/* ---------- PR7 slice 3: post-window arithmetic patterns ---------- */
/*
 * The HK workbook has 18 / 21 table-calc fields whose formulas wrap a
 * `LOOKUP(...)` in one of two outer-arithmetic shapes. Neither fits the
 * minimal grammar above (they're compositions over an aggregated and a
 * windowed value), so `classifyCalcFormula` correctly returns
 * `calc_formula_shape_unsupported`. But these two shapes are so common
 * and so repeatable that it's worth recognizing them explicitly — once
 * the Rust typed spec grows a post-window arithmetic primitive, the
 * lowering of these patterns will just consume the structured tuples
 * emitted here.
 *
 * Shape A — "PY % diff":
 *
 *   (ZN(SUM([m])) - LOOKUP(ZN(SUM([m])), N)) / ABS(LOOKUP(ZN(SUM([m])), N))
 *
 * Shape B — "FIRST() diff, null-when-zero":
 *
 *   iif((LOOKUP(ZN(<agg>), FIRST()) / ZN(<agg>)) - 1 = 0,
 *       null,
 *       (LOOKUP(ZN(<agg>), FIRST()) / ZN(<agg>)) - 1)
 *
 * where `<agg>` is either `SUM([m])` or a bare `([m])`
 * aggregation-omitted reference. Both shapes ignore surrounding
 * whitespace and line breaks; the matchers below are tolerant.
 */

export type RecognizedCompositePattern =
  | {
      kind: 'py_diff'
      base: ClassifiedCalcAggregation
      offset: number
    }
  | {
      kind: 'first_diff_nullable'
      base: ClassifiedCalcAggregation
    }
  // Shape B variant: wraps the Shape B positive branch with an additional
  // `iif(abs(ratio - 1) > 100, null, ...)` guard. Observed on HK
  // `Total Net - GC % Diff` and `Color Net % Diff`. Downstream lowering
  // is identical to `first_diff_nullable` except the outer result is
  // additionally nulled when `|ratio - 1| > 100`.
  | {
      kind: 'first_diff_nullable_abs_capped'
      base: ClassifiedCalcAggregation
      absCap: number
    }

export function recognizeCompositePattern(
  formula: string,
): { ok: true; pattern: RecognizedCompositePattern } | { ok: false } {
  const f = normalizeFormula(formula)
  if (!f) return { ok: false }

  // Shape A: `( ZN(<agg>) - LOOKUP(ZN(<agg>), N) ) / ABS(LOOKUP(ZN(<agg>), N))`
  // Tolerant of inner/outer parenthesization variations. The "current"
  // term is either `ZN(<agg>)` directly OR `LOOKUP(ZN(<agg>), 0)` (HK
  // authors use the self-lookup form for `PH PY % Diff` and
  // `Efficiency PY % Diff`; the two are semantically identical since
  // `LOOKUP(<expr>, 0)` is the current-row value).
  const shapeACurrentTerm = String.raw`(?:ZN\s*\(\s*(.+?)\s*\)|LOOKUP\s*\(\s*ZN\s*\(\s*(.+?)\s*\)\s*,\s*0\s*\))`
  const shapeA = f.match(
    new RegExp(
      `^\\(?\\s*${shapeACurrentTerm}\\s*-\\s*LOOKUP\\s*\\(\\s*ZN\\s*\\(\\s*(.+?)\\s*\\)\\s*,\\s*(-?\\d+)\\s*\\)\\s*\\)?\\s*\\/\\s*ABS\\s*\\(\\s*LOOKUP\\s*\\(\\s*ZN\\s*\\(\\s*(.+?)\\s*\\)\\s*,\\s*(-?\\d+)\\s*\\)\\s*\\)\\s*$`,
      'i',
    ),
  )
  if (shapeA) {
    // Either `aggA` (direct `ZN(<agg>)`) or `aggAself` (inside `LOOKUP(ZN(.), 0)`) is set;
    // the remaining captures are the two offset-N lookups and their matching aggregations.
    const [, aggA, aggAself, aggB, offA, aggC, offB] = shapeA
    const currentAggRaw = aggA ?? aggAself
    if (currentAggRaw) {
      const bA = parseAggregation(currentAggRaw)
      const bB = parseAggregation(aggB)
      const bC = parseAggregation(aggC)
      if (
        bA &&
        bB &&
        bC &&
        JSON.stringify(bA) === JSON.stringify(bB) &&
        JSON.stringify(bB) === JSON.stringify(bC) &&
        offA === offB
      ) {
        const n = Number(offA)
        if (Number.isFinite(n) && Number.isInteger(n) && n !== 0) {
          return { ok: true, pattern: { kind: 'py_diff', base: bA, offset: n } }
        }
      }
    }
  }

  // Shape B capped: wraps Shape B's positive branch with an additional
  // `iif(abs(ratio - 1) > <cap>, null, ratio - 1)` guard. Observed on
  // HK `Total Net - GC % Diff` (cap 100) and `Color Net % Diff`
  // (cap 100). We match the full outer `iif((LOOKUP(...)/ZN(...)) - 1 =
  // 0, null, iif(abs(...) > <cap>, null, ...))` shape and pin the
  // aggregation across all four inner references.
  const shapeBCapped = f.match(
    /^IIF\s*\(\s*\(\s*LOOKUP\s*\(\s*ZN\s*\(\s*(.+?)\s*\)\s*,\s*FIRST\s*\(\s*\)\s*\)\s*\/\s*ZN\s*\(\s*(.+?)\s*\)\s*\)\s*-\s*1\s*=\s*0\s*,\s*NULL\s*,\s*IIF\s*\(\s*ABS\s*\(\s*\(\s*LOOKUP\s*\(\s*ZN\s*\(\s*(.+?)\s*\)\s*,\s*FIRST\s*\(\s*\)\s*\)\s*\/\s*ZN\s*\(\s*(.+?)\s*\)\s*\)\s*-\s*1\s*\)\s*>\s*(\d+(?:\.\d+)?)\s*,\s*NULL\s*,\s*\(\s*LOOKUP\s*\(\s*ZN\s*\(\s*(.+?)\s*\)\s*,\s*FIRST\s*\(\s*\)\s*\)\s*\/\s*ZN\s*\(\s*(.+?)\s*\)\s*\)\s*-\s*1\s*\)\s*\)$/i,
  )
  if (shapeBCapped) {
    const [, a1, a2, a3, a4, capRaw, a5, a6] = shapeBCapped
    const parsed = [a1, a2, a3, a4, a5, a6].map(parseAggregation)
    if (parsed.every(Boolean)) {
      const [b1, ...rest] = parsed as ClassifiedCalcAggregation[]
      if (rest.every((b) => JSON.stringify(b) === JSON.stringify(b1))) {
        const cap = Number(capRaw)
        if (Number.isFinite(cap) && cap > 0) {
          return {
            ok: true,
            pattern: { kind: 'first_diff_nullable_abs_capped', base: b1, absCap: cap },
          }
        }
      }
    }
  }

  // Shape B: `iif((LOOKUP(ZN(<agg>), FIRST()) / ZN(<agg>)) - 1 = 0,
  //              null,
  //              (LOOKUP(ZN(<agg>), FIRST()) / ZN(<agg>)) - 1)`
  const shapeB = f.match(
    /^IIF\s*\(\s*\(\s*LOOKUP\s*\(\s*ZN\s*\(\s*(.+?)\s*\)\s*,\s*FIRST\s*\(\s*\)\s*\)\s*\/\s*ZN\s*\(\s*(.+?)\s*\)\s*\)\s*-\s*1\s*=\s*0\s*,\s*NULL\s*,\s*\(\s*LOOKUP\s*\(\s*ZN\s*\(\s*(.+?)\s*\)\s*,\s*FIRST\s*\(\s*\)\s*\)\s*\/\s*ZN\s*\(\s*(.+?)\s*\)\s*\)\s*-\s*1\s*\)$/i,
  )
  if (shapeB) {
    const [_, a1, a2, a3, a4] = shapeB
    const [b1, b2, b3, b4] = [a1, a2, a3, a4].map(parseAggregation)
    if (
      b1 &&
      b2 &&
      b3 &&
      b4 &&
      JSON.stringify(b1) === JSON.stringify(b2) &&
      JSON.stringify(b2) === JSON.stringify(b3) &&
      JSON.stringify(b3) === JSON.stringify(b4)
    ) {
      return { ok: true, pattern: { kind: 'first_diff_nullable', base: b1 } }
    }
  }

  return { ok: false }
}

/**
 * PR7 table-calc-lookup-semantics sub-slice (step 1 of calc-ref
 * resolution): given a calc-field *name* (e.g. `Calculation_126382321869328384`
 * or its caption `Cuts / Day`) and an index from calc-field name to
 * raw formula, attempt to classify the referenced calc's own formula
 * as a simple aggregation `<AGG>([phys])`. Returns the resolved
 * `ClassifiedCalcAggregation` when the reference chain ends at a
 * pure aggregate, or null when (a) the index has no entry, (b) the
 * formula is empty, (c) the formula is not a pure aggregate, or (d)
 * a reference cycle is detected.
 *
 * This is intentionally narrow — it does NOT resolve ratio-of-aggregates
 * (`SUM(a)/SUM(b)`), FIXED LODs (`{FIXED ... : <agg>}`), or arbitrary
 * calc expressions. Those are distinct widenings (tracked as
 * `pr7-table-calc-lookup-semantics` follow-up sub-slices): each needs a
 * corresponding TypedDerivedMeasure / TypedLodMeasure primitive before
 * the producer can emit SQL for them.
 *
 * The `calcIndex` is a map from every aliasable token Tableau authors
 * use to reference a calc — the bare column `name` (`Calculation_xxx`)
 * AND its `caption` (`Cuts / Day`). Authors use either interchangeably
 * in formulas (`ZN([Calculation_xxx])` vs `ZN([Cuts / Day])`).
 */
export function resolveBareAggregationViaCalcIndex(
  calcRefName: string,
  calcIndex: ReadonlyMap<string, string>,
  visited: Set<string> = new Set(),
): ClassifiedCalcAggregation | null {
  const key = calcRefName.trim()
  if (!key) return null
  if (visited.has(key)) return null
  visited.add(key)
  const formula = calcIndex.get(key)
  if (!formula) return null
  const normalized = normalizeFormula(formula)
  if (!normalized) return null
  // Pure <AGG>([phys]) alias.
  const direct = normalized.match(
    /^(SUM|AVG|MIN|MAX|COUNT|COUNTD)\s*\(\s*\[([^\]]+)\]\s*\)$/i,
  )
  if (direct) {
    const aggRaw = direct[1].toUpperCase() as
      | 'SUM'
      | 'AVG'
      | 'MIN'
      | 'MAX'
      | 'COUNT'
      | 'COUNTD'
    const inner = direct[2].trim()
    if (!inner) return null
    // One-hop chase: if the inner bracket is itself another calc name,
    // recurse — but only unwrap when the outer aggregation is the
    // identity-preserving pair (`SUM(SUM(x))` is NOT legal Tableau, so
    // this branch is the only safe chase). The recursion handles
    // caption ↔ internal-name aliasing as well.
    if (calcIndex.has(inner)) {
      return resolveBareAggregationViaCalcIndex(inner, calcIndex, visited)
    }
    return { kind: 'explicit', agg: aggRaw, field: inner }
  }
  return null
}

/**
 * PR7 table-calc-lookup-semantics (step 2 — row-level expression
 * base): a minimal expression parser that recognizes the small
 * family of row-level calc formulas HK authors use as the
 * unaggregated input to a `SUM(...)`-over-calc-ref. Returns an
 * authoring-layer AST intentionally disjoint from the Rust
 * `TypedCalcExpr` wire shape — the caller translates one to the
 * other because this parser only recognizes the narrow row-level
 * grammar that never mentions parameters, iif, or case.
 *
 * Recognized grammar (after whitespace/newline normalization):
 *
 *   row_expr   := term (('+'|'-') term)*
 *   term       := zn_term | field
 *   zn_term    := 'ZN' '(' row_expr ')'           (nested ZN allowed)
 *   field      := '[' <non-bracket-chars> ']'
 *
 * Multiplication/division intentionally excluded here — any such
 * shape is either an aggregate ratio (handled by
 * `resolveBareAggregationViaCalcIndex` rejecting as non_simple_alias)
 * or outside the currently-observed HK inventory.
 *
 * Returns a compact AST the caller converts to a Rust-bound
 * `TypedCalcExpr`. Returns `null` for anything outside the grammar
 * (including any aggregation function reference, any LOOKUP/FIRST,
 * any FIXED LOD marker `{...}`, or any parameter reference `[@...]`).
 */
export type RowLevelExprNode =
  | { kind: 'field'; name: string }
  | { kind: 'zn'; inner: RowLevelExprNode }
  | { kind: 'binary'; op: '+' | '-'; lhs: RowLevelExprNode; rhs: RowLevelExprNode }

export function parseRowLevelExpr(raw: string): RowLevelExprNode | null {
  const s = normalizeFormula(raw)
  if (!s) return null
  // Reject anything with aggregation-like keywords so we never wrap an
  // already-aggregated expression in another SUM. The grammar above
  // intentionally excludes these; this pre-check makes rejection
  // explicit and future-proof against small grammar extensions.
  if (
    /\b(SUM|AVG|MIN|MAX|COUNT|COUNTD|LOOKUP|FIRST|LAST|INDEX|SIZE|RUNNING_\w+|WINDOW_\w+)\b/i.test(
      s,
    )
  ) {
    return null
  }
  // Reject FIXED LOD expressions ({FIXED ...:...}) and parameter
  // references ([@...]). Both are out of scope for row-level
  // expression measures.
  if (/\{[\s\S]*?\bFIXED\b/i.test(s)) return null
  if (/\[@/.test(s)) return null
  let i = 0
  function peek(): string {
    return s[i] ?? ''
  }
  function skipSpaces(): void {
    while (i < s.length && /\s/.test(s[i]!)) i++
  }
  function parseField(): RowLevelExprNode | null {
    skipSpaces()
    if (peek() !== '[') return null
    const end = s.indexOf(']', i + 1)
    if (end < 0) return null
    const name = s.slice(i + 1, end).trim()
    if (!name) return null
    i = end + 1
    return { kind: 'field', name }
  }
  function parseTerm(): RowLevelExprNode | null {
    skipSpaces()
    // `ZN ( ... )` wrapper.
    const znMatch = s.slice(i).match(/^ZN\s*\(/i)
    if (znMatch) {
      i += znMatch[0].length
      const inner = parseExpr()
      if (!inner) return null
      skipSpaces()
      if (peek() !== ')') return null
      i++
      return { kind: 'zn', inner }
    }
    // Parenthesized subexpression.
    if (peek() === '(') {
      i++
      const inner = parseExpr()
      if (!inner) return null
      skipSpaces()
      if (peek() !== ')') return null
      i++
      return inner
    }
    return parseField()
  }
  function parseExpr(): RowLevelExprNode | null {
    let lhs = parseTerm()
    if (!lhs) return null
    while (true) {
      skipSpaces()
      const c = peek()
      if (c !== '+' && c !== '-') break
      i++
      const rhs = parseTerm()
      if (!rhs) return null
      lhs = { kind: 'binary', op: c, lhs, rhs }
    }
    return lhs
  }
  const expr = parseExpr()
  skipSpaces()
  if (i !== s.length) return null
  return expr
}

/**
 * PR7 table-calc-lookup-semantics (step 3 — aggregated-ratio base):
 * a minimal expression parser for calc-field formulas whose formula
 * is already aggregated — a tree over `SUM([phys])` leaves combined
 * with `+`, `-`, `*`, `/`, `ABS(...)`, `ZN(...)`, numeric literals, and an
 * optional outer `IIF(ABS(<ratio>) > <cap>, NULL, <body>)` capping
 * guard. Shapes observed on HK `Calculation_1760344506439352320`
 * (TotalNet/GC/ProdHrs capped ratio).
 *
 * Returns an authoring-layer AST disjoint from `TypedDerivedExpr` so
 * the producer can synthesize one `TypedMeasure { agg:'SUM', field:
 * <phys> }` per leaf and rewrite leaves to `measure_ref` aliases
 * before handing the tree to the Rust compiler. The parser REJECTS
 * anything outside the grammar (FIXED LODs, row-level refs, other
 * aggregations, table-calc functions, etc.) by returning `null`, so
 * rejection telemetry stays per-formula-shape actionable.
 */
export type AggregatedCalcExprNode =
  | { kind: 'sum_field'; field: string }
  | { kind: 'agg_field'; agg: 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'; field: string }
  // `{ FIXED [dim1],[dim2] : <agg>([field]) }` — LOD leaf. The outer
  // containing expression must apply an aggregation (typically SUM) to
  // this leaf, since the LOD produces a per-partition value that gets
  // replicated per source row and then re-aggregated at view grain.
  // The producer synthesizes a `TypedFixedLod { source_level: true }`
  // per unique leaf and replaces the leaf with a `measure_ref` to an
  // outer `TypedMeasure { field: <lod_output_column>, agg: 'SUM' }`.
  | {
      kind: 'fixed_lod'
      partitionBy: string[]
      agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
      field: string
    }
  // `{ AVG({ FIXED DATETRUNC('<grain>', [date]) : <agg>([field]) }) }`
  // inside an outer aggregate. Tableau uses this to compare the
  // current view-grain aggregate against the full-partition average of
  // fixed period totals. The producer lowers this to a helper
  // `WINDOW_AVG(<agg field>)` and references that helper from the
  // derived expression.
  | {
      kind: 'window_agg_fixed_lod'
      windowAgg: 'AVG'
      partitionBy: Array<
        | { kind: 'field'; field: string }
        | { kind: 'date_trunc'; grain: 'day' | 'week' | 'month' | 'quarter' | 'year'; field: string }
      >
      agg: 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
      field: string
    }
  | { kind: 'lit'; value: number }
  | {
      kind: 'bin_op'
      op: '+' | '-' | '*' | '/'
      lhs: AggregatedCalcExprNode
      rhs: AggregatedCalcExprNode
    }
  | { kind: 'abs'; inner: AggregatedCalcExprNode }
  | { kind: 'coalesce'; inner: AggregatedCalcExprNode; fallback: AggregatedCalcExprNode }
  // `SUM(<row-level expr>)` — row-level expression aggregated with
  // SUM. Example: `SUM(ZN([x]) + ZN([y]))`. The producer synthesizes a
  // `measure_input` calc field whose expression is the row-level
  // expression and a SUM measure over that field, replacing this leaf
  // with a `measure_ref` to the SUM alias.
  | { kind: 'sum_row_expr'; inner: RowLevelExprNode }
  | { kind: 'sum_dim_expr'; inner: DimensionCalcExprNode }
  | {
      kind: 'iif_abs_gt_cap_then_null'
      inner: AggregatedCalcExprNode
      cap: number
      body: AggregatedCalcExprNode
    }

export function parseAggregatedCalcExpr(raw: string): AggregatedCalcExprNode | null {
  const s = normalizeFormula(raw)
  if (!s) return null
  // Bail on parameter refs — out of scope here. (FIXED LODs ARE
  // in-scope; handled by `parseFixedLodLeaf` below.) Callers that want
  // to distinguish parameter-ref / LOD-unsupported-shape / other use
  // `aggregatedCalcExprBlockerHint`.
  if (/\[@/.test(s)) return null
  // Table-calc / unsupported aggregations are also out of scope.
  // NOTE: AVG/MIN/MAX/COUNT/COUNTD intentionally remain rejected as
  // outer aggregations (only SUM is wired end-to-end), but they ARE
  // accepted as the INNER aggregation of a FIXED LOD leaf — so the
  // pre-check runs before any LOD tokenization.
  if (/\b(LOOKUP|FIRST|LAST|INDEX|SIZE|RUNNING_\w+|WINDOW_\w+)\s*\(/i.test(s)) {
    return null
  }

  let i = 0
  const peek = (): string => s[i] ?? ''
  const skipSpaces = (): void => {
    while (i < s.length && /\s/.test(s[i]!)) i++
  }
  const eat = (lit: string): boolean => {
    skipSpaces()
    if (s.slice(i, i + lit.length).toUpperCase() === lit.toUpperCase()) {
      i += lit.length
      return true
    }
    return false
  }

  function parseField(): AggregatedCalcExprNode | null {
    skipSpaces()
    if (peek() !== '[') return null
    const match = s.slice(i).match(/^\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?/)
    if (!match) return null
    const name = String(match[2] ?? match[1]).trim()
    if (!name) return null
    i += match[0].length
    return { kind: 'sum_field', field: name }
  }

  function parseFixedLodLeaf(): AggregatedCalcExprNode | null {
    // `{ FIXED [dim1],[dim2],... : <agg>([field]) }`
    skipSpaces()
    if (peek() !== '{') return null
    const savedI = i
    i++
    if (!eat('FIXED')) {
      i = savedI
      return null
    }
    // Partition-by list: zero or more bracketed dim refs separated by
    // commas, terminated by `:`.
    const partitionBy: string[] = []
    skipSpaces()
    while (peek() !== ':' && i < s.length) {
      const f = parseField()
      if (!f || f.kind !== 'sum_field') {
        i = savedI
        return null
      }
      partitionBy.push(f.field)
      skipSpaces()
      if (peek() === ',') {
        i++
        skipSpaces()
      }
    }
    if (peek() !== ':') {
      i = savedI
      return null
    }
    i++
    skipSpaces()
    // Inner aggregation: `<AGG>([field])`.
    const aggMatch = s.slice(i).match(/^(SUM|AVG|MIN|MAX|COUNT|COUNTD)\s*\(/i)
    if (!aggMatch) {
      i = savedI
      return null
    }
    const agg = aggMatch[1].toUpperCase() as
      | 'SUM'
      | 'AVG'
      | 'MIN'
      | 'MAX'
      | 'COUNT'
      | 'COUNTD'
    i += aggMatch[0].length
    const innerField = parseField()
    if (!innerField || innerField.kind !== 'sum_field') {
      i = savedI
      return null
    }
    skipSpaces()
    if (peek() !== ')') {
      i = savedI
      return null
    }
    i++
    skipSpaces()
    if (peek() !== '}') {
      i = savedI
      return null
    }
    i++
    if (partitionBy.length === 0) {
      // `{FIXED : agg(x)}` — grand-total LOD, not in current scope.
      i = savedI
      return null
    }
    return { kind: 'fixed_lod', partitionBy, agg, field: innerField.field }
  }

  function parseNestedWindowAggFixedLodLeaf(): AggregatedCalcExprNode | null {
    skipSpaces()
    if (peek() !== '{') return null
    const src = s.slice(i)
    const nested = src.match(
      /^\{\s*(AVG|AVERAGE|MEAN)\s*\(\s*\{\s*FIXED\s+([\s\S]+?)\s*:\s*(SUM|AVG|MIN|MAX|COUNT|COUNTD)\s*\(\s*\[([^\]]+)\]\s*\)\s*\}\s*\)\s*\}/i,
    )
    if (!nested) return null
    const partitionSrc = String(nested[2] ?? '').trim()
    const parts = splitTopLevelArgs(partitionSrc)
    if (!parts || parts.length === 0) return null
    const partitionBy: Array<
      | { kind: 'field'; field: string }
      | { kind: 'date_trunc'; grain: 'day' | 'week' | 'month' | 'quarter' | 'year'; field: string }
    > = []
    for (const part of parts) {
      const field = extractBracketField(part)
      if (field) {
        partitionBy.push({ kind: 'field', field })
        continue
      }
      const dt = part.match(
        /^(?:DATETRUNC|DATEPART)\s*\(\s*['"](?:yy|yyyy|year|qq|qtr|quarter|mm|mon|month|wk|week|dd|day)['"]\s*,\s*\[([^\]]+)\]\s*\)$/i,
      )
      if (dt) {
        const grainRaw = part.match(/['"]([^'"]+)['"]/i)?.[1]?.toLowerCase() ?? ''
        const grain =
          /^(yy|yyyy|year)$/.test(grainRaw)
            ? 'year'
            : /^(qq|qtr|quarter)$/.test(grainRaw)
              ? 'quarter'
              : /^(mm|mon|month)$/.test(grainRaw)
                ? 'month'
                : /^(wk|week)$/.test(grainRaw)
                  ? 'week'
                  : 'day'
        const fieldName = String(dt[1] ?? '').trim()
        if (!fieldName) return null
        partitionBy.push({ kind: 'date_trunc', grain, field: fieldName })
        continue
      }
      return null
    }
    const agg = nested[3].toUpperCase() as 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
    const field = String(nested[4] ?? '').trim()
    if (!field) return null
    i += nested[0].length
    return { kind: 'window_agg_fixed_lod', windowAgg: 'AVG', partitionBy, agg, field }
  }

  function parseAtom(): AggregatedCalcExprNode | null {
    skipSpaces()
    if (peek() === '-') {
      i++
      const inner = parseAtom()
      if (!inner) return null
      return { kind: 'bin_op', op: '-', lhs: { kind: 'lit', value: 0 }, rhs: inner }
    }
    // SUM([phys]), SUM({FIXED ...}), or SUM(<row-level expr>)
    const sumMatch = s.slice(i).match(/^SUM\s*\(/i)
    if (sumMatch) {
      i += sumMatch[0].length
      skipSpaces()
      const savedInner = i
      // Try: FIXED LOD leaf first, then bare field, else fall back to
      // a row-level expression (e.g. `SUM(ZN([x])+ZN([y]))`). The
      // row-level path extracts the balanced substring up to the
      // matching `)` and delegates to `parseRowLevelExpr`.
      if (peek() === '{') {
        const nestedWindowAgg = parseNestedWindowAggFixedLodLeaf()
        if (nestedWindowAgg) {
          skipSpaces()
          if (peek() !== ')') return null
          i++
          return nestedWindowAgg
        }
        const lod = parseFixedLodLeaf()
        if (!lod) return null
        skipSpaces()
        if (peek() !== ')') return null
        i++
        return lod
      }
      // Quick probe: a single bracketed identifier followed by `)` is
      // a bare sum_field. Use lookahead without consuming.
      const bareField = s.slice(i).match(/^\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?\s*\)/)
      if (bareField) {
        const name = String(bareField[2] ?? bareField[1]).trim()
        i += bareField[0].length - 1 // leave `)` for outer consumption below
        if (!name) return null
        skipSpaces()
        if (peek() !== ')') return null
        i++
        return { kind: 'sum_field', field: name }
      }
      // Fall back: extract the balanced `(...)` and parse as row-level.
      let depth = 1
      let j = savedInner
      while (j < s.length && depth > 0) {
        const ch = s[j]!
        if (ch === '(') depth++
        else if (ch === ')') {
          depth--
          if (depth === 0) break
        }
        j++
      }
      if (depth !== 0) return null
      const innerSrc = s.slice(savedInner, j)
      const rowExpr = parseRowLevelExpr(innerSrc)
      if (rowExpr) {
        i = j + 1
        return { kind: 'sum_row_expr', inner: rowExpr }
      }
      const dimExpr = parseDimensionCalcFormula(innerSrc)
      if (!dimExpr) return null
      i = j + 1
      return { kind: 'sum_dim_expr', inner: dimExpr }
    }
    // Non-SUM aggregate leaves inside derived aggregate expressions,
    // e.g. `SUM(IF ... END) / COUNTD([RespID])`.
    const aggLiteralMatch = s.slice(i).match(/^(AVG|MIN|MAX)\s*\(\s*(-?\d+(?:\.\d+)?)\s*\)/i)
    if (aggLiteralMatch) {
      i += aggLiteralMatch[0].length
      const value = Number(aggLiteralMatch[2])
      if (!Number.isFinite(value)) return null
      return { kind: 'lit', value }
    }
    const sumZeroLiteralMatch = s.slice(i).match(/^SUM\s*\(\s*(-?0(?:\.0+)?)\s*\)/i)
    if (sumZeroLiteralMatch) {
      i += sumZeroLiteralMatch[0].length
      return { kind: 'lit', value: 0 }
    }
    const aggFieldMatch = s.slice(i).match(/^(AVG|MIN|MAX|COUNT|COUNTD)\s*\(\s*\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?\s*\)/i)
    if (aggFieldMatch) {
      i += aggFieldMatch[0].length
      const agg = aggFieldMatch[1].toUpperCase() as 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNTD'
      const field = String(aggFieldMatch[3] ?? aggFieldMatch[2] ?? '').trim()
      if (!field) return null
      return { kind: 'agg_field', agg, field }
    }
    // Bare `{FIXED ...}` at atom level (no outer SUM authored).
    if (peek() === '{') {
      const nestedWindowAgg = parseNestedWindowAggFixedLodLeaf()
      if (nestedWindowAgg) return nestedWindowAgg
      const lod = parseFixedLodLeaf()
      if (lod) return lod
      return null
    }
    // ZN(<expr>) = COALESCE(<expr>, 0)
    const znMatch = s.slice(i).match(/^ZN\s*\(/i)
    if (znMatch) {
      i += znMatch[0].length
      const inner = parseExpr()
      if (!inner) return null
      skipSpaces()
      if (peek() !== ')') return null
      i++
      return { kind: 'coalesce', inner, fallback: { kind: 'lit', value: 0 } }
    }
    // ABS(<expr>)
    const absMatch = s.slice(i).match(/^ABS\s*\(/i)
    if (absMatch) {
      i += absMatch[0].length
      const inner = parseExpr()
      if (!inner) return null
      skipSpaces()
      if (peek() !== ')') return null
      i++
      return { kind: 'abs', inner }
    }
    // IIF(ABS(<ratio>) > <cap>, NULL, <body>) — outer capping guard.
    const iifMatch = s.slice(i).match(/^IIF\s*\(/i)
    if (iifMatch) {
      const savedI = i
      i += iifMatch[0].length
      // Parse cond: ABS(<inner>) > <cap>
      if (!eat('ABS')) {
        i = savedI
        return null
      }
      skipSpaces()
      if (peek() !== '(') {
        i = savedI
        return null
      }
      i++
      const inner = parseExpr()
      if (!inner) {
        i = savedI
        return null
      }
      skipSpaces()
      if (peek() !== ')') {
        i = savedI
        return null
      }
      i++
      skipSpaces()
      if (peek() !== '>') {
        i = savedI
        return null
      }
      i++
      skipSpaces()
      const numMatch = s.slice(i).match(/^(\d+(?:\.\d+)?)/)
      if (!numMatch) {
        i = savedI
        return null
      }
      const cap = Number(numMatch[1])
      if (!Number.isFinite(cap) || cap <= 0) {
        i = savedI
        return null
      }
      i += numMatch[0].length
      skipSpaces()
      if (peek() !== ',') {
        i = savedI
        return null
      }
      i++
      if (!eat('NULL')) {
        i = savedI
        return null
      }
      skipSpaces()
      if (peek() !== ',') {
        i = savedI
        return null
      }
      i++
      const body = parseExpr()
      if (!body) {
        i = savedI
        return null
      }
      skipSpaces()
      if (peek() !== ')') {
        i = savedI
        return null
      }
      i++
      return { kind: 'iif_abs_gt_cap_then_null', inner, cap, body }
    }
    // Parenthesized subexpression.
    if (peek() === '(') {
      i++
      const inner = parseExpr()
      if (!inner) return null
      skipSpaces()
      if (peek() !== ')') return null
      i++
      return inner
    }
    // Numeric literal.
    const num = s.slice(i).match(/^(-?\d+(?:\.\d+)?)/)
    if (num) {
      const v = Number(num[1])
      if (Number.isFinite(v)) {
        i += num[0].length
        return { kind: 'lit', value: v }
      }
    }
    return null
  }

  function parseMulDiv(): AggregatedCalcExprNode | null {
    let lhs = parseAtom()
    if (!lhs) return null
    while (true) {
      skipSpaces()
      const c = peek()
      if (c !== '*' && c !== '/') break
      i++
      const rhs = parseAtom()
      if (!rhs) return null
      lhs = { kind: 'bin_op', op: c, lhs, rhs }
    }
    return lhs
  }

  function parseExpr(): AggregatedCalcExprNode | null {
    let lhs = parseMulDiv()
    if (!lhs) return null
    while (true) {
      skipSpaces()
      const c = peek()
      if (c !== '+' && c !== '-') break
      i++
      const rhs = parseMulDiv()
      if (!rhs) return null
      lhs = { kind: 'bin_op', op: c, lhs, rhs }
    }
    return lhs
  }

  const out = parseExpr()
  skipSpaces()
  if (i !== s.length) return null
  // Reject a plain `sum_field` leaf — that's handled by
  // `resolveBareAggregationViaCalcIndex`'s existing pure-alias path
  // and should not route through the derived-base machinery. A bare
  // `fixed_lod` leaf is legal here (the producer synthesizes an
  // outer SUM over the LOD output column automatically).
  if (out && out.kind === 'sum_field') return null
  return out
}

/**
 * Classify *why* `parseAggregatedCalcExpr` rejected a formula, for
 * surfacing in rejection telemetry. Returns one of:
 *   - `'fixed_lod'` — formula contains a `{FIXED ...}` LOD marker.
 *   - `'parameter_ref'` — formula contains a `[@param]` reference.
 *   - `'unsupported_shape'` — everything else (generic catch-all).
 * Non-aggregated (row-level) formulas collapse into
 * `'unsupported_shape'` here; they're handled by
 * `parseRowLevelExpr` at the caller.
 */
export function aggregatedCalcExprBlockerHint(
  raw: string,
): 'fixed_lod' | 'parameter_ref' | 'unsupported_shape' {
  const s = normalizeFormula(raw)
  // Retained for callers that want to surface a FIXED-LOD-specific
  // hint even after `parseAggregatedCalcExpr` itself was widened to
  // accept FIXED LOD leaves — if parse still fails AND a FIXED
  // marker is present, the blocker is a LOD shape outside our
  // supported subset (e.g. INCLUDE/EXCLUDE/nested LODs).
  if (/\{[\s\S]*?\bFIXED\b/i.test(s)) return 'fixed_lod'
  if (/\[@/.test(s)) return 'parameter_ref'
  return 'unsupported_shape'
}

/**
 * Build a caption → physical-field-name index from a workbook IR's
 * `field_dictionary.columns`. Used by the authoring-to-typed lowering
 * so a measure authored as the caption `Hair Cut Count` resolves to
 * its physical column `haircutcount` when checking whether a composite
 * pattern's inferred base (`SUM([haircutcount])`) is already authored
 * on the worksheet.
 *
 * Only *non-calc* columns (no `calculation.formula`) are included —
 * calc captions are handled by the calc-formula index instead.
 * Returns both captions (`Hair Cut Count`) and raw bracketed names
 * (`haircutcount`) so lookups tolerate either authoring form.
 */
export function buildFieldCaptionIndexFromWorkbookIr(
  workbookIr: unknown,
): Map<string, string> {
  const out = new Map<string, string>()
  if (!workbookIr || typeof workbookIr !== 'object') return out
  const fd = (workbookIr as { field_dictionary?: unknown }).field_dictionary
  if (!fd || typeof fd !== 'object') return out
  const cols = (fd as { columns?: unknown }).columns
  if (!Array.isArray(cols)) return out
  for (const c of cols) {
    if (!c || typeof c !== 'object') continue
    const calc = (c as { calculation?: unknown }).calculation
    if (calc && typeof calc === 'object') {
      const formula = (calc as { formula?: unknown }).formula
      if (typeof formula === 'string' && formula.trim()) continue
    }
    const nameRaw = (c as { name?: unknown }).name
    const captionRaw = (c as { caption?: unknown }).caption
    if (typeof nameRaw !== 'string') continue
    const physical = nameRaw.trim().replace(/^\[/, '').replace(/\]$/, '').trim()
    if (!physical) continue
    out.set(physical, physical)
    if (typeof captionRaw === 'string' && captionRaw.trim()) {
      out.set(captionRaw.trim(), physical)
    }
  }
  return out
}

/**
 * Minimal parser for dimension-level Tableau calc formulas. Supports:
 *   - field refs: `[name]`
 *   - string literals: `"..."` or `'...'`
 *   - numeric literals
 *   - NULL
 *   - iif(cond, then, else[, unknown])
 *   - contains(str, lit), starts_with, ends_with
 *   - equality `=`, `==`, `<>`, `!=`
 *
 * Returns a `TypedCalcExpr`-shaped tree or null if the formula is
 * outside this grammar. Kept intentionally narrow — it exists only
 * to unblock dimension-level calcs like
 * `iif(contains([City State Zip],", CA"),"West","East")`.
 */
export type DimensionCalcBinOp =
  | 'add' | 'sub' | 'mul' | 'div'
  | 'eq' | 'ne' | 'lt' | 'le' | 'gt' | 'ge'
  | 'and' | 'or'

export type DimensionCalcExprNode =
  | { kind: 'field_ref'; name: string }
  | { kind: 'param_ref'; name: string }
  | { kind: 'string_lit'; value: string }
  | { kind: 'number_lit'; value: number }
  | { kind: 'bool_lit'; value: boolean }
  | { kind: 'null' }
  | { kind: 'not'; inner: DimensionCalcExprNode }
  | { kind: 'neg'; inner: DimensionCalcExprNode }
  | { kind: 'binary'; op: DimensionCalcBinOp; lhs: DimensionCalcExprNode; rhs: DimensionCalcExprNode }
  | {
      kind: 'func'
      name: string
      args: DimensionCalcExprNode[]
    }
  | {
      kind: 'case_when'
      branches: { when: DimensionCalcExprNode; then: DimensionCalcExprNode }[]
      else_: DimensionCalcExprNode | null
    }
  | {
      kind: 'case_switch'
      subject: DimensionCalcExprNode
      branches: { when: DimensionCalcExprNode; then: DimensionCalcExprNode }[]
      else_: DimensionCalcExprNode | null
    }

export function parseDimensionCalcFormula(raw: string): DimensionCalcExprNode | null {
  const src = String(raw ?? '')
  if (!src.trim()) return null
  // Strip /*...*/ and //... comments; preserve string literals.
  const s = src
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/\/\/[^\n]*/g, ' ')
  let i = 0
  function skip(): void {
    while (i < s.length && /\s/.test(s[i]!)) i++
  }
  function peek(n = 0): string {
    return s[i + n] ?? ''
  }
  function matchKeyword(kw: string): boolean {
    skip()
    const seg = s.slice(i, i + kw.length)
    if (seg.toLowerCase() !== kw.toLowerCase()) return false
    const after = s[i + kw.length]
    if (after && /[A-Za-z_0-9]/.test(after)) return false
    i += kw.length
    return true
  }
  function peekKeyword(kw: string): boolean {
    skip()
    const seg = s.slice(i, i + kw.length)
    if (seg.toLowerCase() !== kw.toLowerCase()) return false
    const after = s[i + kw.length]
    if (after && /[A-Za-z_0-9]/.test(after)) return false
    return true
  }
  function parseString(): string | null {
    skip()
    const q = peek()
    if (q !== '"' && q !== "'") return null
    i++
    let out = ''
    while (i < s.length && s[i] !== q) {
      if (s[i] === '\\' && i + 1 < s.length) {
        out += s[i + 1]
        i += 2
      } else {
        out += s[i]
        i++
      }
    }
    if (peek() !== q) return null
    i++
    return out
  }
  function parseBracketName(): string | null {
    skip()
    if (peek() !== '[') return null
    const end = s.indexOf(']', i + 1)
    if (end < 0) return null
    const name = s.slice(i + 1, end).trim()
    if (!name) return null
    i = end + 1
    return name
  }
  function parseNumber(): number | null {
    skip()
    const m = s.slice(i).match(/^\d+(?:\.\d+)?/)
    if (!m) return null
    i += m[0].length
    return Number(m[0])
  }
  function parseCaseExpr(): DimensionCalcExprNode | null {
    // CASE (<expr>)? WHEN <x> THEN <y> ... [ELSE <z>] END
    skip()
    // Try to parse a subject for `CASE <expr> WHEN ...`
    const save = i
    let subject: DimensionCalcExprNode | null = null
    if (!peekKeyword('when')) {
      subject = parseOr()
      if (!subject) {
        i = save
        return null
      }
    }
    const branches: { when: DimensionCalcExprNode; then: DimensionCalcExprNode }[] = []
    while (peekKeyword('when')) {
      matchKeyword('when')
      const w = parseOr()
      if (!w) return null
      if (!matchKeyword('then')) return null
      const t = parseOr()
      if (!t) return null
      branches.push({ when: w, then: t })
    }
    let else_: DimensionCalcExprNode | null = null
    if (matchKeyword('else')) {
      else_ = parseOr()
      if (!else_) return null
    }
    if (!matchKeyword('end')) return null
    if (branches.length === 0) return null
    if (subject) return { kind: 'case_switch', subject, branches, else_ }
    return { kind: 'case_when', branches, else_ }
  }
  function parseIfExpr(): DimensionCalcExprNode | null {
    // Tableau row-level conditional: IF <cond> THEN <expr> [ELSE <expr>] END.
    const cond = parseOr()
    if (!cond) return null
    if (!matchKeyword('then')) return null
    const thenExpr = parseOr()
    if (!thenExpr) return null
    const branches: { when: DimensionCalcExprNode; then: DimensionCalcExprNode }[] = [
      { when: cond, then: thenExpr },
    ]
    while (matchKeyword('elseif')) {
      const elseifCond = parseOr()
      if (!elseifCond) return null
      if (!matchKeyword('then')) return null
      const elseifThen = parseOr()
      if (!elseifThen) return null
      branches.push({ when: elseifCond, then: elseifThen })
    }
    let else_: DimensionCalcExprNode | null = null
    if (matchKeyword('else')) {
      else_ = parseOr()
      if (!else_) return null
    }
    if (!matchKeyword('end')) return null
    return { kind: 'case_when', branches, else_ }
  }
  function parsePrimary(): DimensionCalcExprNode | null {
    skip()
    if (peek() === '(') {
      i++
      const inner = parseOr()
      skip()
      if (peek() !== ')') return null
      i++
      return inner
    }
    // Parameter reference: [Parameters].[X]
    if (peek() === '[') {
      const save = i
      const first = parseBracketName()
      if (first === null) return null
      skip()
      if (peek() === '.') {
        i++
        skip()
        const second = parseBracketName()
        if (second === null) {
          i = save
          return null
        }
        if (first === 'Parameters') return { kind: 'param_ref', name: second }
        return { kind: 'field_ref', name: second }
      }
      return { kind: 'field_ref', name: first }
    }
    const str = parseString()
    if (str !== null) return { kind: 'string_lit', value: str }
    if (matchKeyword('null')) return { kind: 'null' }
    if (matchKeyword('true')) return { kind: 'bool_lit', value: true }
    if (matchKeyword('false')) return { kind: 'bool_lit', value: false }
    if (matchKeyword('case')) return parseCaseExpr()
    if (matchKeyword('if')) return parseIfExpr()
    if (matchKeyword('not')) {
      const inner = parseCmp()
      if (!inner) return null
      return { kind: 'not', inner }
    }
    // Function call
    const idMatch = s.slice(i).match(/^([A-Za-z_][A-Za-z_0-9]*)\s*\(/)
    if (idMatch) {
      const name = idMatch[1].toLowerCase()
      i += idMatch[0].length
      const args: DimensionCalcExprNode[] = []
      skip()
      if (peek() === ')') {
        i++
      } else {
        while (true) {
          const a = parseOr()
          if (!a) return null
          args.push(a)
          skip()
          if (peek() === ',') {
            i++
            continue
          }
          if (peek() === ')') {
            i++
            break
          }
          return null
        }
      }
      return { kind: 'func', name, args }
    }
    if (peek() === '-') {
      i++
      const inner = parsePrimary()
      if (!inner) return null
      return { kind: 'neg', inner }
    }
    if (peek() === '+') {
      i++
      return parsePrimary()
    }
    const n = parseNumber()
    if (n !== null) return { kind: 'number_lit', value: n }
    return null
  }
  function parseMulDiv(): DimensionCalcExprNode | null {
    let lhs = parsePrimary()
    if (!lhs) return null
    while (true) {
      skip()
      const c = peek()
      if (c !== '*' && c !== '/') break
      i++
      const rhs = parsePrimary()
      if (!rhs) return null
      lhs = { kind: 'binary', op: c === '*' ? 'mul' : 'div', lhs, rhs }
    }
    return lhs
  }
  function parseAddSub(): DimensionCalcExprNode | null {
    let lhs = parseMulDiv()
    if (!lhs) return null
    while (true) {
      skip()
      const c = peek()
      if (c !== '+' && c !== '-') break
      i++
      const rhs = parseMulDiv()
      if (!rhs) return null
      lhs = { kind: 'binary', op: c === '+' ? 'add' : 'sub', lhs, rhs }
    }
    return lhs
  }
  function parseCmp(): DimensionCalcExprNode | null {
    const lhs = parseAddSub()
    if (!lhs) return null
    skip()
    const two = s.slice(i, i + 2)
    if (two === '<=' || two === '>=' || two === '<>' || two === '!=' || two === '==') {
      i += 2
      const rhs = parseAddSub()
      if (!rhs) return null
      const op: DimensionCalcBinOp =
        two === '<=' ? 'le' : two === '>=' ? 'ge' : two === '<>' || two === '!=' ? 'ne' : 'eq'
      return { kind: 'binary', op, lhs, rhs }
    }
    const one = peek()
    if (one === '=' || one === '<' || one === '>') {
      i++
      const rhs = parseAddSub()
      if (!rhs) return null
      const op: DimensionCalcBinOp = one === '=' ? 'eq' : one === '<' ? 'lt' : 'gt'
      return { kind: 'binary', op, lhs, rhs }
    }
    return lhs
  }
  function parseAnd(): DimensionCalcExprNode | null {
    let lhs = parseCmp()
    if (!lhs) return null
    while (matchKeyword('and')) {
      const rhs = parseCmp()
      if (!rhs) return null
      lhs = { kind: 'binary', op: 'and', lhs, rhs }
    }
    return lhs
  }
  function parseOr(): DimensionCalcExprNode | null {
    let lhs = parseAnd()
    if (!lhs) return null
    while (matchKeyword('or')) {
      const rhs = parseAnd()
      if (!rhs) return null
      lhs = { kind: 'binary', op: 'or', lhs, rhs }
    }
    return lhs
  }
  const out = parseOr()
  skip()
  if (i !== s.length) return null
  return out
}

const unquoteTableauDisplayLiteral = (raw: string): string => {
  const s = String(raw ?? '').trim()
  if (s.length >= 2) {
    const first = s[0]
    const last = s[s.length - 1]
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return s.slice(1, -1)
    }
  }
  return s
}

const normalizeTableauDisplayLabel = (raw: string): string =>
  unquoteTableauDisplayLiteral(raw)
    .replace(/\s+/g, ' ')
    .replace(/\s*\|\s*/g, ' | ')
    .trim()

const calcFormulaIndexIdentityKeys = (raw: string): string[] => {
  const out: string[] = []
  const seen = new Set<string>()
  const add = (value: string): void => {
    const v = String(value ?? '').trim()
    if (!v || seen.has(v)) return
    seen.add(v)
    out.push(v)
  }
  const trimmed = String(raw ?? '').trim()
  add(trimmed)
  add(trimmed.replace(/^\[/, '').replace(/\]$/, '').trim())
  add(unquoteTableauDisplayLiteral(trimmed))
  add(normalizeTableauDisplayLabel(trimmed))
  return out
}

/**
 * Build a calc-formula index from a workbook IR. Indexes every calc
 * field by BOTH its bracketed internal name (`Calculation_xxx`) and
 * its caption (`Cuts / Day`), so `resolveBareAggregationViaCalcIndex`
 * can chase either form authors use in formulas.
 *
 * Expects `workbookIr.field_dictionary.columns` to be an array of
 * objects with at least `{ name, caption, calculation: { formula } }`.
 * Tolerant of missing / non-object entries.
 */
export function buildCalcFormulaIndexFromWorkbookIr(
  workbookIr: unknown,
): Map<string, string> {
  const out = new Map<string, string>()
  if (!workbookIr || typeof workbookIr !== 'object') return out
  const fd = (workbookIr as { field_dictionary?: unknown }).field_dictionary
  if (!fd || typeof fd !== 'object') return out
  const cols = (fd as { columns?: unknown }).columns
  if (!Array.isArray(cols)) return out
  for (const c of cols) {
    if (!c || typeof c !== 'object') continue
    const calc = (c as { calculation?: unknown }).calculation
    if (!calc || typeof calc !== 'object') continue
    const formula = (calc as { formula?: unknown }).formula
    if (typeof formula !== 'string' || !formula.trim()) continue
    const nameRaw = (c as { name?: unknown }).name
    const captionRaw = (c as { caption?: unknown }).caption
    if (typeof nameRaw === 'string') {
      for (const key of calcFormulaIndexIdentityKeys(nameRaw)) {
        out.set(key, formula)
      }
    }
    if (typeof captionRaw === 'string' && captionRaw.trim()) {
      for (const key of calcFormulaIndexIdentityKeys(captionRaw)) {
        out.set(key, formula)
      }
    }
    const aliases = (c as { aliases?: unknown }).aliases
    if (Array.isArray(aliases)) {
      for (const alias of aliases) {
        if (!alias || typeof alias !== 'object') continue
        const keyRaw = (alias as { key?: unknown }).key
        const valueRaw = (alias as { value?: unknown }).value
        if (typeof keyRaw === 'string') {
          for (const key of calcFormulaIndexIdentityKeys(keyRaw)) {
            out.set(key, formula)
          }
        }
        if (typeof valueRaw === 'string') {
          for (const key of calcFormulaIndexIdentityKeys(valueRaw)) {
            out.set(key, formula)
          }
        }
      }
    }
  }
  return out
}
